import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { useState } from "react";
import { Check, ArrowRight, Sparkles, Star } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { useCurrency } from "@/lib/ecom/currency";
import {
  PLANS,
  getPlanMonthly,
  formatPlanPrice,
  defaultPlanPriceText,
} from "@/components/marketing/pricing-data";
import { alternateLinkList } from "@/lib/i18n/locale";

const greekPlanCopy: Record<string, string> = {
  Free: "Δωρεάν",
  Teams: "Ομάδες",
  Enterprise: "Επιχειρήσεις",
  "All the essentials to start.": "Όλα τα απαραίτητα για να ξεκινήσετε.",
  "Get started": "Ξεκινήστε",
  "1 digital business card": "1 ψηφιακή επαγγελματική κάρτα",
  "Unlimited card taps & shares": "Απεριόριστα αγγίγματα και κοινοποιήσεις κάρτας",
  "QR + Wallet card": "Κάρτα QR + Wallet",
  "Basic contacts CRM (250)": "Βασικό CRM επαφών (250)",
  "1 booking page": "1 σελίδα κρατήσεων",
  "Email + chat support": "Υποστήριξη μέσω email και συνομιλίας",
  "Everything you need to grow — solo.": "Όλα όσα χρειάζεστε για να αναπτυχθείτε — ατομικά.",
  "14 Days Free Trial": "Δωρεάν δοκιμή 14 ημερών",
  "Everything in Free, plus:": "Όλα όσα περιλαμβάνει το Δωρεάν, και επιπλέον:",
  "AI Brain — memory, scans & enrichment": "AI Brain — μνήμη, σαρώσεις και εμπλουτισμός",
  "AI email drafts (200 / month)": "Προσχέδια email με AI (200 / μήνα)",
  "Unlimited contacts & pipelines": "Απεριόριστες επαφές και ροές πωλήσεων",
  "Bookings with payments": "Κρατήσεις με πληρωμές",
  "Contracts & e-signature (10 / mo)": "Συμβάσεις και ηλεκτρονική υπογραφή (10 / μήνα)",
  "Automations & workflows": "Αυτοματισμοί και ροές εργασίας",
  "Advanced analytics": "Προηγμένα αναλυτικά στοιχεία",
  "For modern sales & operations teams.": "Για σύγχρονες ομάδες πωλήσεων και λειτουργιών.",
  "Everything in Pro AI, plus:": "Όλα όσα περιλαμβάνει το Pro AI, και επιπλέον:",
  "Shared workspaces & roles": "Κοινόχρηστοι χώροι εργασίας και ρόλοι",
  "Team-branded digital cards": "Ψηφιακές κάρτες με την ταυτότητα της ομάδας σας",
  "Shared CRM & pipelines": "Κοινόχρηστο CRM και ροές πωλήσεων",
  "Bulk NFC card ordering": "Μαζικές παραγγελίες καρτών NFC",
  "Admin console & SSO add-on": "Κονσόλα διαχείρισης και πρόσθετο SSO",
  "Priority support": "Υποστήριξη κατά προτεραιότητα",
  "For organizations that need the works.": "Για οργανισμούς που χρειάζονται μια ολοκληρωμένη λύση.",
  "Talk to sales": "Μιλήστε με το τμήμα πωλήσεων",
  "Everything in Teams, plus:": "Όλα όσα περιλαμβάνει το Ομάδες, και επιπλέον:",
  "SSO / SCIM provisioning": "Παροχή πρόσβασης μέσω SSO / SCIM",
  "Custom contracts & DPA": "Εξατομικευμένες συμβάσεις και DPA",
  "Dedicated success manager": "Αποκλειστικός υπεύθυνος επιτυχίας πελατών",
  "Custom integrations & API": "Εξατομικευμένες διασυνδέσεις και API",
  "99.9% SLA & advanced security": "SLA 99,9% και προηγμένη ασφάλεια",
  "Onboarding & training": "Αρχική καθοδήγηση και εκπαίδευση",
};

const translatePlanCopy = (text: string) => greekPlanCopy[text] ?? text;

export const Route = createFileRoute("/el/pricing")({
  head: () => ({
    meta: [
      { title: "Τιμολόγηση — banana.cards" },
      { name: "description", content: "Απλή, διαφανής τιμολόγηση για ιδιώτες και ομάδες. Ξεκινήστε δωρεάν και αναπτυχθείτε με AI όταν το χρειαστείτε." },
      { property: "og:title", content: "Τιμολόγηση banana.cards" },
      { property: "og:description", content: "Δωρεάν για πάντα για ιδιώτες. Pro AI για όσους επενδύουν στη δικτύωση. Πρόγραμμα Ομάδες με χρέωση ανά θέση. Πρόγραμμα Επιχειρήσεις κατόπιν αιτήματος." },
      { property: "og:url", content: "https://banana.cards/el/pricing" },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/pricing", "el"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Είναι το δωρεάν πρόγραμμα πραγματικά δωρεάν για πάντα;", acceptedAnswer: { "@type": "Answer", text: "Ναι. Μπορείτε να χρησιμοποιείτε το banana.cards ως ψηφιακή επαγγελματική κάρτα και βασικό CRM δωρεάν, για πάντα — χωρίς πιστωτική κάρτα." } },
            { "@type": "Question", name: "Χρειάζεται να αγοράσω κάρτα NFC;", acceptedAnswer: { "@type": "Answer", text: "Όχι — η ψηφιακή σας κάρτα λειτουργεί μέσω QR, συνδέσμου, υπογραφής email ή Apple/Google Wallet. Οι κάρτες NFC είναι ένα προαιρετικό premium πρόσθετο." } },
            { "@type": "Question", name: "Μπορώ να αλλάξω πρόγραμμα αργότερα;", acceptedAnswer: { "@type": "Answer", text: "Βεβαίως. Αναβαθμίστε, επιλέξτε μικρότερο πρόγραμμα ή κάντε παύση οποιαδήποτε στιγμή από τις ρυθμίσεις του χώρου εργασίας σας." } },
            { "@type": "Question", name: "Πώς λειτουργεί η χρήση AI;", acceptedAnswer: { "@type": "Answer", text: "Το Pro AI περιλαμβάνει 200 email με AI, 100 βελτιώσεις και 50 σαρώσεις καρτών τον μήνα. Το πρόγραμμα Ομάδες προσφέρει υψηλότερα όρια, ενώ το πρόγραμμα Επιχειρήσεις προσφέρει απεριόριστη χρήση." } },
            { "@type": "Question", name: "Προσφέρετε εκπτώσεις για φοιτητές ή μη κερδοσκοπικούς οργανισμούς;", acceptedAnswer: { "@type": "Answer", text: "Ναι — επικοινωνήστε με την ομάδα μας και θα σας βοηθήσουμε να ξεκινήσετε." } },
            { "@type": "Question", name: "Είναι ασφαλή τα δεδομένα μου;", acceptedAnswer: { "@type": "Answer", text: "Όλα τα δεδομένα κρυπτογραφούνται κατά τη μεταφορά και την αποθήκευση. Τα προγράμματα Ομάδες και Επιχειρήσεις υποστηρίζουν SSO και υπογεγραμμένες συμφωνίες επεξεργασίας δεδομένων (DPA)." } },
          ],
        }),
      },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const [interval, setInterval] = useState<"month" | "year">("year");
  const [seats, setSeats] = useState(3);
  const { country } = useCurrency();

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-4 pt-20 pb-12 text-center md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Τιμολόγηση</p>
        <h1 className="mx-auto mt-3 max-w-3xl font-display text-3xl leading-[1.1] md:text-5xl">
          Ξεκινήστε δωρεάν. Αναπτυχθείτε όταν <em className="italic">είστε έτοιμοι.</em>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
          Χωρίς πιστωτική κάρτα. Ακυρώστε οποιαδήποτε στιγμή. Οι τιμές προσαρμόζονται αυτόματα στην περιοχή σας.
        </p>

        {/* Billing toggle */}
        <div className="mt-10 inline-flex items-center gap-2 rounded-full bg-muted p-1">
          <button
            type="button"
            onClick={() => setInterval("month")}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
              interval === "month" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Μηνιαία
          </button>
          <button
            type="button"
            onClick={() => setInterval("year")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
              interval === "year" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Ετήσια
            <span className="rounded-full bg-brand-lime px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-brand-lime-foreground">
              Κερδίστε με την ετήσια χρέωση
            </span>
          </button>
        </div>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const isFree = p.key === "free";
            const isEnterprise = p.key === "enterprise";
            const isPaid = p.key === "pro_ai" || p.key === "teams";
            const paidKey = (p.key === "pro_ai" || p.key === "teams") ? p.key : null;
            const priceText = paidKey
              ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, interval))
              : defaultPlanPriceText("pro_ai", interval);
            const firstYearPriceText = paidKey
              ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, interval) / 2)
              : "";
            const seatTotalText = paidKey
              ? formatPlanPrice(
                  country,
                  (interval === "year"
                    ? getPlanMonthly(paidKey, country.code, "year") / 2
                    : getPlanMonthly(paidKey, country.code, interval)) * seats,
                )
              : "";
            void isPaid;

            return (
              <div
                key={p.key}
                className={`relative flex flex-col rounded-2xl bg-muted p-6 transition ${
                  p.popular
                    ? "ring-2 ring-foreground"
                    : ""
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 left-6 rounded-full bg-foreground px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider text-background">
                    Δημοφιλέστερο
                  </span>
                )}

                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-card">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="text-lg font-bold">{translatePlanCopy(p.name)}</h3>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{translatePlanCopy(p.tagline)}</p>

                {/* Price */}
                <div className="mt-6 min-h-[80px]">
                  {isFree ? (
                    <>
                      <p className="font-display text-5xl leading-none">Δωρεάν</p>
                      <p className="mt-1 text-xs text-muted-foreground">Για πάντα, για ιδιώτες.</p>
                    </>
                  ) : isEnterprise ? (
                    <>
                      <p className="font-display text-4xl leading-none">Εξατομικευμένη</p>
                      <p className="mt-2 text-xs text-muted-foreground">Τιμολόγηση και SLA στα μέτρα σας.</p>
                    </>
                  ) : (
                    <>
                      {interval === "year" ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-display text-lg leading-none tracking-tight text-muted-foreground line-through sm:text-xl">
                            {priceText}
                          </span>
                          <div className="flex flex-wrap items-baseline gap-x-1">
                            <span className="font-display text-4xl leading-none tracking-tight sm:text-5xl">
                              {firstYearPriceText}
                            </span>
                            <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                              /{p.perSeat ? "θέση/" : ""}μήνα
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                          <span className="font-display text-4xl leading-none tracking-tight sm:text-5xl">
                            {priceText}
                          </span>
                          <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                            /{p.perSeat ? "θέση/" : ""}μήνα
                          </span>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {interval === "year" ? "Ετήσια χρέωση" : "Μηνιαία χρέωση"}
                      </p>
                      {interval === "year" ? (
                        <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-brand-lime/30 px-2 py-0.5 text-[10px] font-bold text-brand-lime-foreground">
                          Έκπτωση 50% τον πρώτο χρόνο
                        </p>
                      ) : (
                        <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-brand-lime-foreground">
                          Κερδίστε έκπτωση 50% επιλέγοντας ετήσια χρέωση
                        </p>
                      )}
                      {p.perSeat && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Θέσεις</span>
                          <div className="flex items-center overflow-hidden rounded-full bg-card ring-1 ring-foreground/10">
                            <button aria-label="Μείωση θέσεων" onClick={() => setSeats((s) => Math.max(3, s - 1))} className="h-7 w-7 text-sm font-bold hover:bg-card">–</button>
                            <span className="w-8 border-x border-foreground/10 text-center text-xs font-bold">{seats}</span>
                            <button aria-label="Αύξηση θέσεων" onClick={() => setSeats((s) => Math.min(500, s + 1))} className="h-7 w-7 text-sm font-bold hover:bg-card">+</button>
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            ≈ {seatTotalText}/μήνα
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* CTA */}
                <CtaLink
                  to={isEnterprise ? "/el/contact" : "/signup"}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                    p.popular
                      ? "bg-brand-lime text-brand-lime-foreground hover:scale-[1.02]"
                      : "bg-foreground text-background hover:opacity-90"
                  }`}
                >
                  {translatePlanCopy(p.cta)}
                  <ArrowRight className="h-4 w-4" />
                </CtaLink>

                {/* Features */}
                <ul className="mt-6 space-y-2.5 border-t border-foreground/5 pt-6">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${i === 0 && !f.startsWith("Everything") ? "" : ""}`}>
                      {f.startsWith("Everything in") ? (
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{translatePlanCopy(f)}</span>
                      ) : (
                        <>
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                          <span>{translatePlanCopy(f)}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          </div>
        </div>
      </section>

      {/* Compare strip */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="rounded-3xl bg-card p-8 md:p-12">
          <div className="grid items-center gap-8 md:grid-cols-[1.4fr_2fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Για ολόκληρη την ομάδα σας</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Σχεδιασμένο για <em className="italic">ομάδες που γνωρίζουν ανθρώπους.</em>
              </h2>
              <p className="mt-4 text-sm text-muted-foreground">
                Αξιοποιήστε κάρτες με την εταιρική σας ταυτότητα, κοινόχρηστο CRM και επικοινωνίες παρακολούθησης με AI σε ολόκληρο τον οργανισμό σας. Εκπτώσεις για 25+ θέσεις.
              </p>
              <div className="mt-6 flex gap-2">
                <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background hover:opacity-90">
                  Μιλήστε με το τμήμα πωλήσεων <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {[
                "Πρότυπα καρτών με την ταυτότητα της ομάδας σας",
                "Κοινόχρηστες επαφές και ροές πωλήσεων",
                "Δικαιώματα διαχείρισης και SSO",
                "Μαζικές παραγγελίες καρτών NFC",
                "Προσαρμοσμένα ονόματα τομέα και εξατομικευμένοι σύνδεσμοι",
                "Υποστήριξη κατά προτεραιότητα μέσω τηλεφώνου και συνομιλίας",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 rounded-xl bg-muted p-3 text-sm">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="font-semibold">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background">
        <div className="mx-auto max-w-3xl px-4 py-24 md:px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Συχνές ερωτήσεις</p>
        <h2 className="mt-2 text-center font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Οι ερωτήσεις σας, <em className="italic">με απαντήσεις.</em>
        </h2>
        <div className="mt-10 space-y-3">
          {[
            { q: "Είναι το δωρεάν πρόγραμμα πραγματικά δωρεάν για πάντα;", a: "Ναι. Μπορείτε να χρησιμοποιείτε το banana.cards ως ψηφιακή επαγγελματική κάρτα και βασικό CRM δωρεάν, για πάντα — χωρίς πιστωτική κάρτα." },
            { q: "Χρειάζεται να αγοράσω κάρτα NFC;", a: "Όχι — η ψηφιακή σας κάρτα λειτουργεί μέσω QR, συνδέσμου, υπογραφής email ή Apple/Google Wallet. Οι κάρτες NFC είναι ένα προαιρετικό premium πρόσθετο." },
            { q: "Μπορώ να αλλάξω πρόγραμμα αργότερα;", a: "Βεβαίως. Αναβαθμίστε, επιλέξτε μικρότερο πρόγραμμα ή κάντε παύση οποιαδήποτε στιγμή από τις ρυθμίσεις του χώρου εργασίας σας." },
            { q: "Πώς λειτουργεί η χρήση AI;", a: "Το Pro AI περιλαμβάνει 200 email με AI, 100 βελτιώσεις και 50 σαρώσεις καρτών τον μήνα. Το πρόγραμμα Ομάδες προσφέρει υψηλότερα όρια, ενώ το πρόγραμμα Επιχειρήσεις προσφέρει απεριόριστη χρήση." },
            { q: "Προσφέρετε εκπτώσεις για φοιτητές ή μη κερδοσκοπικούς οργανισμούς;", a: "Ναι — επικοινωνήστε με την ομάδα μας και θα σας βοηθήσουμε να ξεκινήσετε." },
            { q: "Είναι ασφαλή τα δεδομένα μου;", a: "Όλα τα δεδομένα κρυπτογραφούνται κατά τη μεταφορά και την αποθήκευση. Τα προγράμματα Ομάδες και Επιχειρήσεις υποστηρίζουν SSO και υπογεγραμμένες συμφωνίες επεξεργασίας δεδομένων (DPA)." },
          ].map((f, i) => (
            <details key={i} className="group rounded-2xl bg-muted p-5">
              <summary className="flex cursor-pointer items-center justify-between text-sm font-bold">
                {f.q}
                <span className="ml-3 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-card transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
          <div className="mx-auto inline-flex items-center gap-1 rounded-full bg-background/10 px-3 py-1 text-xs font-bold text-brand-lime ring-1 ring-background/10">
            <Star className="h-3 w-3 fill-current" /> 4,9 / 5 από περισσότερες από 12.000 αξιολογήσεις
          </div>
          <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            Δοκιμάστε το banana.cards <em className="italic text-brand-lime">δωρεάν</em> για 14 ημέρες.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              Ξεκινήστε δωρεάν
            </CtaLink>
            <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              Μιλήστε με το τμήμα πωλήσεων
            </Link>
          </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
